/* ══════════════════════════════════════════════════════════════
   Currency Data Service — USD/INR and major pairs
   Fetches exchange rates from backend API
   ══════════════════════════════════════════════════════════════ */

import { apiFetch } from './api-client';

export interface CurrencyRate {
    pair: string;
    name: string;
    rate: number;
    change: number;
    pChange: number;
    direction: 'up' | 'down' | 'flat';
    high: number;
    low: number;
    timestamp: string;
}

export interface CurrencySummary {
    rates: CurrencyRate[];
    usdInr: CurrencyRate | null;
    eurInr: CurrencyRate | null;
    gbpInr: CurrencyRate | null;
    jpyInr: CurrencyRate | null;
}

/** Fetch all currency rates */
export async function fetchCurrencyRates(): Promise<CurrencyRate[]> {
    try {
        const result = await apiFetch<{ rates: CurrencyRate[] }>('/currency', { cacheTTL: 60 });
        return (result.data.rates || []).map(r => ({
            ...r,
            direction: r.change > 0 ? 'up' as const : r.change < 0 ? 'down' as const : 'flat' as const,
        }));
    } catch (err) {
        console.warn('Failed to fetch currency rates:', err);
        return [];
    }
}

/** Fetch currency summary with named accessors */
export async function fetchCurrencySummary(): Promise<CurrencySummary> {
    const rates = await fetchCurrencyRates();

    const find = (pair: string): CurrencyRate | null =>
        rates.find(r => r.pair === pair || r.pair.replace('/', '') === pair) || null;

    return {
        rates,
        usdInr: find('USD/INR') || find('USDINR'),
        eurInr: find('EUR/INR') || find('EURINR'),
        gbpInr: find('GBP/INR') || find('GBPINR'),
        jpyInr: find('JPY/INR') || find('JPYINR'),
    };
}

/** Get rupee strength indicator based on USD/INR movement */
export function getRupeeStrength(usdInr: CurrencyRate): {
    label: string;
    direction: 'strengthening' | 'weakening' | 'stable';
} {
    // When USD/INR goes down, rupee strengthens
    if (usdInr.pChange < -0.2) return { label: 'Rupee Strengthening', direction: 'strengthening' };
    if (usdInr.pChange > 0.2) return { label: 'Rupee Weakening', direction: 'weakening' };
    return { label: 'Rupee Stable', direction: 'stable' };
}
